import React, { useEffect, useState } from 'react';
import async from 'async';
import moment from 'moment';
import 'moment/min/locales.min';
import {
    BackgroundTimetable,
    ProgramTypeButtonsContainer,
    SearchContainer,
    TabElement,
    Timezone,
    TimezoneInRuler,
} from './style/style';
import { useLocation, withRouter } from 'react-router-dom';

import Filter from '../../components/Filter';
import ProgramTypeButtons from '../../components/General/ProgramTypeButtons';
import Search from '../../components/General/Search';
import * as palette from '../../components/General/Variables';
import ThemeContext from '../../components/Theme/ThemeContext';
import { WithTimezone } from '../Timezone/context';
import { getAllProgramFavoritesForEvent, getMarkedAsFavorites } from '../../services/api/data';
import { default as Store, getString } from '../../services/api/store.js';
import ListView from './components/ListView';
import Timetable from './components/timetable';
import {
    getDays,
    getSessionsFromDay,
    getFilterTypes,
    getGroupingOptions,
    filterProgram,
} from './services/programme';
import { getItem } from '../../services/api/graphQlRepository';
import AccessRestrictions from '../AccessRestrictions';
import ProgrammeTabsAndToolbar from './components/ProgrammeTabsAndToolbar';
import { programmeViewTypes } from './constants/index';
import VerticalTimetable from './components/verticalTimetable';
import BrandingBanner from '../../components/General/BrandingBanner';
import { getImageSource } from '../../services/api/db';

const Programme = props => {
    const [tabsData, setTabsData] = useState([
        {
            day: {},
            elements: [],
            timerange: {},
        },
    ]);

    const [activeTab, setActiveTab] = useState(0);
    const [loading, setLoading] = useState(true);
    const [filters, setFilters] = useState([]);
    const [skipTimeframes, setSkipTimeframes] = useState(false);
    const [page, setPage] = useState({});
    const [filterTypes, setFilterTypes] = useState([]);
    const [grouping, setGrouping] = useState(null);
    const [openViewDropDown, setOpenViewDropDown] = useState(false);
    const [showFilter, setShowFilter] = useState(false);
    const [viewType, setViewType] = useState(programmeViewTypes.HORIZONTAL_VIEW);
    const [backgroundUrl, setBackgroundUrl] = useState(null);
    const [titleOnBanner, setTitleOnBanner] = useState(null);
    const { timezone, pageId, pageName } = props;
    const { timeslotToTimezone, selectedTimezone } = timezone;
    const { search } = useLocation();

    const queryParams = new URLSearchParams(search);

    useEffect(() => {
        if (!pageId) {
            return;
        }

        getProgramInfo();
        if (props.disableScroll) {
            props.disableScroll(true);
        }

        return () => {
            if (props.disableScroll) {
                props.disableScroll(false);
            }
            if (props.handleSidepanel) {
                props.handleSidepanel(false);
            }
        };
    }, [pageId, selectedTimezone, pageName]);

    const hasDetailOpen = () => {
        return queryParams.has('type') || queryParams.has('pageType');
    };

    useEffect(() => {
        if (props.handleSidepanel) {
            props.handleSidepanel(backgroundUrl ? false : hasDetailOpen() || showFilter);
        }

        if (hasDetailOpen()) {
            setShowFilter(false);
        }
    }, [search, backgroundUrl]);

    const getDayIndex = (days, inputDate) => {
        let date;
        if (!inputDate) {
            date = moment();
        } else {
            date = moment(inputDate);
        }

        const { selectedTimezone } = props.timezone;
        if (selectedTimezone === 'event') {
            const timezone = Store.argument.offsetTimezone;
            date = date.utcOffset(timezone);
        }

        return days.findIndex(day => moment.utc(day.start).isSame(date, 'day'));
    };

    const getProgramInfo = () => {
        setLoading(true);
        let skipTimeframesVar = false;

        async.waterfall(
            [
                function (callback) {
                    getItem('pages', pageId, (err, page) => {
                        if (err || !page) {
                            return callback('Page not found');
                        }

                        if (page && page.params && page.params.webLayout) {
                            if (page.params.webLayout === 'horizontalTimetable') {
                                setViewType(programmeViewTypes.HORIZONTAL_VIEW);
                            } else if (page.params.webLayout === 'verticalTimetable') {
                                setViewType(programmeViewTypes.VERTICAL_VIEW);
                            } else if (page.params.webLayout === 'list') {
                                setViewType(programmeViewTypes.LIST_VIEW);
                            }
                        }
                        if (page?.params?.bannerImage) {
                            getImageSource(page?.params?.bannerImage, (err, result) => {
                                if (result) {
                                    setBackgroundUrl(result);
                                }
                            });
                        }
                        if (page?.params?.showPageNameInBanner) {
                            setTitleOnBanner(page?.params?.title);
                            props.setTitle('');
                        } else {
                            props.setTitle();
                        }

                        if (page?.params.skipTimeframes) {
                            setSkipTimeframes(page.params.skipTimeframes);
                            skipTimeframesVar = page.params.skipTimeframes;
                        }

                        setPage(page);

                        callback(null, page);
                    });
                },
                function (page, callback) {
                    getDays(page, timeslotToTimezone, (err, days) => {
                        if (err) {
                            callback(err);
                        } else {
                            callback(null, page, days);
                        }
                    });
                },
                function (page, days, callback) {
                    getFilterTypes(page, (err, filterTypes) => {
                        if (err) {
                            callback(err);
                        } else {
                            callback(null, page, days, filterTypes);
                        }
                    });
                },
                function (page, days, filterTypes, callback) {
                    getGroupingOptions(page, (err, grouping) => {
                        if (err) {
                            callback(err);
                        } else {
                            callback(null, page, days, filterTypes, grouping);
                        }
                    });
                },
                function (page, days, filterTypes, grouping, callback) {
                    let program = days.map(day => {
                        return {
                            day: {
                                start: day.start,
                                id: day.id,
                                name: day.name,
                            },
                            elements: [],
                            timerange: {},
                        };
                    });

                    setTabsData(program);
                    setFilterTypes(filterTypes);
                    setGrouping(grouping);

                    // Get all sessions data
                    async.eachSeries(
                        program,
                        function (item, next) {
                            getSessionsFromDay(
                                page,
                                item.day,
                                skipTimeframesVar,
                                grouping,
                                timeslotToTimezone,
                                (err, sessions, timerange) => {
                                    if (err) {
                                        next(err);
                                    }
                                    item.elements = applyFilters(filters, sessions);
                                    item.fullElements = sessions;
                                    item.timerange = timerange;
                                    next();
                                },
                            );
                        },
                        function (err) {
                            if (err) {
                                callback(err);
                            } else {
                                // Do not display empty day tabs
                                const nonEmptyProgram = program.filter(p => p.fullElements.length);
                                const filteredDays = nonEmptyProgram.map(p => p.day);

                                const dayIndex = getDayIndex(filteredDays);
                                let activeTab = dayIndex === -1 ? 0 : dayIndex;
                                setTabsData(nonEmptyProgram);

                                const type = queryParams.get('type');
                                const objectClass = queryParams.get('objectClass');
                                const objectId = queryParams.get('objectId');

                                // Find correct tab for an opened session
                                if (type && objectClass && objectId) {
                                    getItem('timeslots', objectId, (err, timeslot) => {
                                        if (timeslot) {
                                            timeslot = timeslotToTimezone(timeslot);

                                            const foundIndex = getDayIndex(
                                                filteredDays,
                                                timeslot.start,
                                            );

                                            activeTab = foundIndex === -1 ? activeTab : foundIndex;
                                            setActiveTab(activeTab);
                                        }
                                    });
                                } else {
                                    const foundIndex = program.findIndex(p => {
                                        return (
                                            p &&
                                            p.elements &&
                                            p.elements.length &&
                                            p.elements[0].items &&
                                            p.elements[0].items.length
                                        );
                                    });
                                    setActiveTab(foundIndex);
                                }
                            }
                        },
                    );

                    callback(null);
                },
            ],
            function (err) {
                if (err) {
                    console.log(err);
                }
                setLoading(false);
            },
        );
    };

    const displayFilter = show => {
        setShowFilter(show);
        if (props.handleSidepanel) {
            props.handleSidepanel(backgroundUrl ? false : show);
        }
        if (show) {
            props.history.push(props.match.url);
        }
    };

    const onTabClick = indx => {
        setLoading(true);
        setActiveTab(indx);
        filterCallback(filters, indx);
    };

    const applyFilters = (filter, data, favorites, markedAsFavorites) => {
        return filterProgram(filter, data, favorites, markedAsFavorites);
    };

    const changeViewType = type => {
        onTabClick(activeTab);
        setViewType(type);
    };

    const filterCallback = (filter, forcedIndex, forcedData) => {
        let indx = forcedIndex;
        if (indx === null || indx === undefined) {
            indx = activeTab;
        }

        if (
            (tabsData[indx] && tabsData[indx].elements && tabsData[indx].elements.length) ||
            forcedData
        ) {
            const data = forcedData || tabsData;

            if (filter && filter.myProgram && filter.myProgram === 'my_program') {
                getMarkedAsFavorites(data[indx].fullElements, (err, marked) => {
                    getAllProgramFavoritesForEvent((err, favorites) => {
                        const data = [...tabsData];
                        const validIndexes = [];
                        data.forEach((tab, index) => {
                            tab.elements = applyFilters(
                                filter,
                                tab.fullElements,
                                favorites,
                                marked,
                            );
                            if (tab.elements?.find(item => item.items.length)) {
                                validIndexes.push(index);
                            }
                        });
                        setTabsData(data);
                        setFilters(filter);
                        if (!validIndexes.includes(indx)) {
                            setActiveTab(validIndexes[0] || 0);
                        }
                        setLoading(false);
                    });
                });
            } else {
                const data = [...tabsData];
                const validIndexes = [];
                data.forEach((tab, index) => {
                    tab.elements = applyFilters(filter, tab.fullElements);

                    if (tab.elements?.find(item => item.items.length)) {
                        validIndexes.push(index);
                    }
                });
                setTabsData(data);
                setFilters(filter);

                if (!validIndexes.includes(indx)) {
                    setActiveTab(validIndexes[0] || 0);
                }
                setLoading(false);
            }
        }
    };

    const searchCallback = text => {
        filterCallback({
            ...filters,
            text,
        });
    };

    const renderProgramme = theme => {
        let dateSettings = getString('datetime');
        let datePattern =
            dateSettings && dateSettings.dayMonthDateFormat
                ? dateSettings.dayMonthDateFormat
                : 'D MMMM';
        let locale = dateSettings && dateSettings.locale ? dateSettings.locale : 'en';

        const data = tabsData[activeTab];
        let activeTabIndex = activeTab;
        const tabs = tabsData.map((tab, index) => {
            const elem = tab.day;
            let day = moment.utc(elem.start);
            day.locale(locale);

            const label = elem.name + ' ' + day.format(datePattern);

            const { elements } = tabsData[index];
            const displayTab = elements?.find(item => item.items.length);
            if (!displayTab) {
                return <p />;
            }

            return (
                <TabElement
                    key={`${elem.name}-${elem.id}`}
                    label={label}
                    primary={theme.primary}
                    active={activeTabIndex === index}
                />
            );
        });

        const renderTimezoneInfo = () => {
            const { getTimezoneLabel, showTimezoneSelectDialog } = props.timezone;
            if (viewType === programmeViewTypes.LIST_VIEW)
                return <Timezone onClick={showTimezoneSelectDialog}>{getTimezoneLabel()}</Timezone>;
            return (
                <TimezoneInRuler onClick={showTimezoneSelectDialog} viewType={viewType}>
                    {getTimezoneLabel()}
                </TimezoneInRuler>
            );
        };

        const renderTimetable = handleClickOutside => (
            <BackgroundTimetable style={{ height: 'fit-content' }}>
                {showFilter && (
                    <Filter
                        action={filterCallback}
                        selectedFilters={filters}
                        filterTypes={filterTypes}
                        closeFilter={() => displayFilter(false)}
                    />
                )}
                <Timetable
                    items={data.elements}
                    rangeInterval={data.timerange}
                    filters={filters}
                    selectedTimezone={selectedTimezone}
                    renderTimezoneInfo={renderTimezoneInfo}
                    handleClickOutside={handleClickOutside}
                    activeTab={activeTab}
                    withBrandingBanner={backgroundUrl}
                />
            </BackgroundTimetable>
        );

        const renderVerticalTimetable = handleClickOutside => (
            <BackgroundTimetable>
                {showFilter && (
                    <Filter
                        action={filterCallback}
                        selectedFilters={filters}
                        filterTypes={filterTypes}
                        closeFilter={() => displayFilter(false)}
                    />
                )}
                <VerticalTimetable
                    items={data.elements}
                    rangeInterval={data.timerange}
                    filters={filters}
                    selectedTimezone={selectedTimezone}
                    renderTimezoneInfo={renderTimezoneInfo}
                    activeTab={activeTab}
                    handleClickOutside={handleClickOutside}
                    withBrandingBanner={backgroundUrl}
                />
            </BackgroundTimetable>
        );

        const favoriteFilter = myProgram => {
            filterCallback({
                ...filters,
                myProgram,
            });
        };

        const getNumberOfAppliedFilters = () => {
            if (filters && filters.filters) {
                const filter = filters.filters;
                let count = 0;
                Object.keys(filter).forEach(key => (count += filter[key].length));
                return count;
            }
        };

        const renderListView = () => {
            const isMobile = palette.MIN_TABLET_INT && window.innerWidth < palette.MIN_TABLET_INT;

            let filterContainers = [
                <ProgramTypeButtonsContainer key="0" size={2}>
                    <ProgramTypeButtons
                        alignment={isMobile ? 'left' : 'right'}
                        action={myProgram => {
                            favoriteFilter(myProgram);
                        }}
                        myProgram={filters.myProgram || 'all'}
                        useFavoritesTitle={false}
                    />
                </ProgramTypeButtonsContainer>,
                <SearchContainer key="1" size={10}>
                    <Search
                        action={text => {
                            searchCallback(text);
                        }}
                        live={true}
                        skey={pageId}
                    />
                </SearchContainer>,
            ];

            if (!isMobile) {
                filterContainers.reverse();
            }

            return (
                <BackgroundTimetable>
                    {showFilter && (
                        <Filter
                            action={filterCallback}
                            selectedFilters={filters}
                            filterTypes={filterTypes}
                            closeFilter={() => displayFilter(false)}
                        />
                    )}
                    <ListView
                        items={data.elements}
                        range={data.timerange}
                        filters={filters}
                        groupingOption={grouping.groupingOption}
                    />
                </BackgroundTimetable>
            );
        };

        return (
            <ProgrammeTabsAndToolbar
                tabs={tabs}
                activeTab={activeTab}
                loading={loading || data.elements.length === 0}
                openViewDropDown={openViewDropDown}
                skipTimeframes={skipTimeframes}
                renderListView={renderListView}
                renderTimetable={renderTimetable}
                renderTimezoneInfo={renderTimezoneInfo}
                renderVerticalTimetable={renderVerticalTimetable}
                onTabClick={onTabClick}
                action={filterCallback}
                selectedFilters={filters}
                viewType={viewType}
                changeViewType={changeViewType}
                onVisibilityChange={() => setOpenViewDropDown(!openViewDropDown)}
                setShowFilter={() => displayFilter(!showFilter)}
                isSidepanelOpened={hasDetailOpen() || showFilter}
                numberOfAppliedFilters={getNumberOfAppliedFilters()}
            />
        );
    };

    return (
        <ThemeContext.Consumer>
            {({ theme }) => (
                <React.Fragment>
                    <AccessRestrictions
                        pageId={pageId}
                        accessRestrictions={page.accessRestrictions}
                        isPasswordProtected={page.isPasswordProtected}
                        password={page.password}
                    >
                        <BrandingBanner
                            notList={viewType !== programmeViewTypes.LIST_VIEW}
                            backgroundUrl={backgroundUrl}
                            viewType={viewType}
                            sidePanelOpened={props.isSidepanelOpened || showFilter}
                            visibleFullSideMenu={props.visibleFullSideMenu}
                            navigationType={props.navigationType}
                            activeTab={activeTab}
                            title={titleOnBanner}
                            setTitleOnHeader={props.setTitle}
                        >
                            {renderProgramme(theme)}
                        </BrandingBanner>
                    </AccessRestrictions>
                </React.Fragment>
            )}
        </ThemeContext.Consumer>
    );
};

export default withRouter(WithTimezone(Programme));
